/**
 * Application Detail Page
 *
 * Displays detailed information about a specific halal certification application:
 * - Application status and timeline
 * - Application data (company info, facility details, products)
 * - Submitted documents
 * - Consultant assignment
 * - Audit scheduling information
 * - Action buttons (edit draft, submit, withdraw)
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Table } from '../../components/common/Table';
import { Alert } from '../../components/common/Alert';
import { ProgressBar } from '../../components/common/ProgressBar';
import { HelpPanel, HelpSection } from '../../components/help';
import { ClipboardList, Users, Calendar, FileCheck } from 'lucide-react';
import { mockApi } from '../../services/mockApi';
import { formatDate, formatDateTime } from '../../utils/formatting';
import type { Application, ApplicationStatus, ApplicationType } from '../../types/application';

export const ApplicationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['common', 'company']);
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadApplication();
  }, [id]);

  const loadApplication = async () => {
    if (!id) {
      setError('Application ID not provided');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await mockApi.getApplication(id);
      if (response.success && response.data) {
        setApplication(response.data);
      } else {
        setError(response.error || 'Application not found');
      }
    } catch (err) {
      console.error('Error loading application:', err);
      setError('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: ApplicationStatus) => {
    const statusMap: Record<ApplicationStatus, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      DRAFT: { variant: 'default', label: t('common:status.draft') },
      SUBMITTED: { variant: 'info', label: t('common:status.submitted') },
      UNDER_REVIEW: { variant: 'info', label: 'Under Review' },
      CONSULTANT_ASSIGNED: { variant: 'info', label: 'Consultant Assigned' },
      PRE_AUDIT_SCHEDULED: { variant: 'warning', label: 'Pre-Audit Scheduled' },
      PRE_AUDIT_COMPLETED: { variant: 'warning', label: 'Pre-Audit Completed' },
      AUDIT_SCHEDULED: { variant: 'warning', label: 'Audit Scheduled' },
      AUDIT_IN_PROGRESS: { variant: 'warning', label: 'Audit In Progress' },
      AUDIT_COMPLETED: { variant: 'warning', label: 'Audit Completed' },
      PENDING_APPROVAL: { variant: 'warning', label: 'Pending Approval' },
      APPROVED: { variant: 'success', label: t('common:status.approved') },
      REJECTED: { variant: 'error', label: t('common:status.rejected') },
      WITHDRAWN: { variant: 'default', label: 'Withdrawn' },
      CANCELLED: { variant: 'error', label: t('common:status.cancelled') },
    };
    const config = statusMap[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeLabel = (type: ApplicationType): string => {
    const typeMap: Record<ApplicationType, string> = {
      NEW_CERTIFICATION: 'New Certification',
      RENEWAL: 'Renewal',
      MODIFICATION: 'Modification',
      RE_CERTIFICATION: 'Re-Certification',
    };
    return typeMap[type];
  };

  const handleEdit = () => {
    alert('Navigate to edit application page');
  };

  const handleSubmit = () => {
    if (window.confirm('Are you sure you want to submit this application? You will not be able to edit it after submission.')) {
      alert('Submit application (mock action)');
    }
  };

  const handleWithdraw = () => {
    if (window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      alert('Withdraw application (mock action)');
    }
  };

  if (loading) {
    return (
      <PageLayout pageTitle="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common:status.loading')}...</div>
        </div>
      </PageLayout>
    );
  }

  if (error || !application) {
    return (
      <PageLayout pageTitle="Application Not Found">
        <Alert type="error" title="Error">
          {error || 'Application not found'}
        </Alert>
        <div className="mt-4">
          <Button variant="outline" onClick={() => alert('Navigate to Applications List')}>
            ← Back to Applications
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={`Application ${application.applicationNumber}`}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:applications.title'), href: '/company/applications' },
        { label: application.applicationNumber },
      ]}
      actions={
        <div className="flex gap-2">
          {application.status === 'DRAFT' && (
            <>
              <Button variant="outline" onClick={handleEdit}>
                {t('common:actions.edit')}
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                Submit Application
              </Button>
            </>
          )}
          {['SUBMITTED', 'UNDER_REVIEW'].includes(application.status) && (
            <Button variant="outline" onClick={handleWithdraw} className="text-red-600">
              Withdraw Application
            </Button>
          )}
        </div>
      }
    >
      {/* Application Status Alert */}
      {application.status === 'DRAFT' && (
        <Alert type="info" className="mb-6">
          This application is in draft status. Complete all required sections and submit when ready.
        </Alert>
      )}
      {application.status === 'REJECTED' && (
        <Alert type="error" className="mb-6">
          This application has been rejected. Please review the feedback below and submit a new application.
        </Alert>
      )}
      {application.status === 'APPROVED' && (
        <Alert type="success" className="mb-6">
          Congratulations! Your application has been approved. Your halal certificate will be issued shortly.
        </Alert>
      )}

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Left Column */}
            <div className="lg:col-span-2 space-y-6">
          {/* Application Overview */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Application Overview</h3>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application Number</dt>
                  <dd className="mt-1 text-sm font-medium">{application.applicationNumber}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="mt-1">
                    <Badge variant="default">{getTypeLabel(application.type)}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created Date</dt>
                  <dd className="mt-1 text-sm">{formatDate(application.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Submitted Date</dt>
                  <dd className="mt-1 text-sm">
                    {application.submittedAt ? formatDate(application.submittedAt) : (
                      <span className="text-gray-400">Not submitted yet</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">{getStatusBadge(application.status)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Completeness</dt>
                  <dd className="mt-1">
                    {application.completenessScore !== undefined ? (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">{application.completenessScore}%</span>
                        </div>
                        <ProgressBar
                          value={application.completenessScore}
                          variant={application.completenessScore === 100 ? 'success' : 'default'}
                          size="sm"
                          showLabel={false}
                        />
                      </div>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Company Information</h3>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                  <dd className="mt-1 text-sm">{application.companyName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Applicant</dt>
                  <dd className="mt-1 text-sm">{application.applicantName}</dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Submitted Documents */}
          {application.documents && application.documents.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Submitted Documents</h3>
                <p className="text-sm text-gray-600 mt-1">{application.documents.length} document(s)</p>
              </CardHeader>
              <CardBody>
                <Table
                  columns={[
                    {
                      key: 'name',
                      header: 'Document Name',
                      render: (doc: any) => (
                        <div>
                          <div className="font-medium text-blue-600 hover:underline cursor-pointer">
                            {doc.name}
                          </div>
                          <div className="text-xs text-gray-500">{doc.type}</div>
                        </div>
                      ),
                    },
                    {
                      key: 'uploadedAt',
                      header: 'Upload Date',
                      render: (doc: any) => (
                        <span className="text-sm">{formatDate(doc.uploadedAt)}</span>
                      ),
                    },
                    {
                      key: 'verified',
                      header: 'Status',
                      render: (doc: any) => (
                        doc.verified ? (
                          <Badge variant="success" size="sm">
                            {t('common:status.verified')}
                          </Badge>
                        ) : (
                          <Badge variant="info" size="sm">
                            {t('common:status.unverified')}
                          </Badge>
                        )
                      ),
                    },
                    {
                      key: 'actions',
                      header: 'Actions',
                      render: (doc: any) => (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => alert(`View document: ${doc.name}`)}
                        >
                          {t('common:actions.view')}
                        </Button>
                      ),
                    },
                  ]}
                  data={application.documents}
                  keyExtractor={(doc) => doc.id}
                />
              </CardBody>
            </Card>
          )}

          {/* Status History */}
          {application.statusHistory && application.statusHistory.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Status History</h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  {application.statusHistory.map((history, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-600"></div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{getStatusBadge(history.status)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDateTime(history.changedAt)} by {history.changedByName}
                        </div>
                        {history.comment && (
                          <div className="text-sm text-gray-600 mt-1">{history.comment}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Consultant Assignment */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Consultant</h3>
            </CardHeader>
            <CardBody>
              {application.assignedConsultantName ? (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      {application.assignedConsultantName.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium">{application.assignedConsultantName}</div>
                      <div className="text-xs text-gray-500">Halal Consultant</div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" fullWidth onClick={() => alert('Contact consultant')}>
                    Contact Consultant
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No consultant assigned yet</div>
              )}
            </CardBody>
          </Card>

          {/* Audit Information */}
          {(application.preAuditId || application.officialAuditId) && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Audit Information</h3>
              </CardHeader>
              <CardBody>
                <dl className="space-y-3">
                  {application.preAuditId && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Pre-Audit</dt>
                      <dd className="mt-1 text-sm">ID: {application.preAuditId}</dd>
                    </div>
                  )}
                  {application.officialAuditId && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Official Audit</dt>
                      <dd className="mt-1 text-sm">ID: {application.officialAuditId}</dd>
                    </div>
                  )}
                  {application.assignedAuditorName && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Assigned Auditor</dt>
                      <dd className="mt-1 text-sm">{application.assignedAuditorName}</dd>
                    </div>
                  )}
                </dl>
              </CardBody>
            </Card>
          )}

          {/* Review Notes */}
          {application.reviewNotes && application.reviewNotes.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Review Notes</h3>
                <p className="text-sm text-gray-600 mt-1">{application.reviewNotes.length} note(s)</p>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {application.reviewNotes.map((note) => (
                    <div key={note.id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{note.authorName}</span>
                        {note.isInternal && (
                          <Badge variant="warning" size="sm">
                            Internal
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-700 mb-1">{note.content}</div>
                      <div className="text-xs text-gray-500">{formatDateTime(note.createdAt)}</div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                {application.status === 'DRAFT' && (
                  <>
                    <Button variant="outline" fullWidth onClick={handleEdit}>
                      {t('common:actions.edit')} Application
                    </Button>
                    <Button variant="primary" fullWidth onClick={handleSubmit}>
                      Submit Application
                    </Button>
                  </>
                )}
                {['SUBMITTED', 'UNDER_REVIEW'].includes(application.status) && (
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={handleWithdraw}
                    className="text-red-600 hover:text-red-700"
                  >
                    Withdraw Application
                  </Button>
                )}
                <Button variant="outline" fullWidth onClick={() => alert('Print application')}>
                  Print Application
                </Button>
                <Button variant="outline" fullWidth onClick={() => alert('Download PDF')}>
                  Download PDF
                </Button>
              </div>
            </CardBody>
          </Card>
            </div>
          </div>
        </div>

        {/* Help Panel */}
        <HelpPanel title="Application Guide" sticky>
          <HelpSection
            icon={ClipboardList}
            title="Application Status"
            description="Track your application progress through each stage of the certification process."
            variant="primary"
          />

          <HelpSection
            icon={Users}
            title="Consultant Assignment"
            description="A dedicated consultant will be assigned to guide you through the certification process once your application is under review."
          />

          <HelpSection
            icon={Calendar}
            title="Audit Scheduling"
            description="Once your application progresses, you'll be able to view and coordinate audit dates with your assigned auditor."
            collapsible
            defaultCollapsed
          >
            <p className="text-xs">
              Audit scheduling typically occurs 5-7 days after consultant assignment. You'll receive email
              notifications when action is needed.
            </p>
          </HelpSection>

          <HelpSection
            icon={FileCheck}
            title="Need Help?"
            description="If you have questions about your application, contact your assigned consultant or reach out to support."
            variant="success"
            cta={{
              label: 'Contact Support',
              href: '#',
            }}
          />
        </HelpPanel>
      </div>
    </PageLayout>
  );
};

export default ApplicationDetailPage;

